import React from "react";

type BartenderHeadProps = {
  /** Rendered width in px; height follows the scene/character aspect. */
  size?: number;
  className?: string;
  /**
   * "scene" (default) = self-contained mini-bar (character + counter + drink),
   * used as the AI-Pick menu icon. "character" = just the bartender, framed
   * tightly with no bar furniture, for dropping into a larger bar scene.
   */
  variant?: "scene" | "character";
};

/**
 * RUSH AI bartender behind the bar — the character stands behind a wooden
 * counter (green neon under-glow, backlit bottle shelf, neon RUSH sign) with
 * a served cocktail on the bar and a shaker mid-shake. All animation is
 * lightweight SMIL (blink, head bob, eye glances, shaker shake bursts,
 * rising bubbles, neon pulse/flicker). No SVG filters/drop-shadows on
 * purpose — those are expensive to rasterize on low-power GPUs.
 */
export default function BartenderHead({
  size = 180,
  className = "",
  variant = "scene",
}: BartenderHeadProps) {
  const isScene = variant === "scene";
  // "character" crops out the bar furniture (bottle shelf, counter, served
  // cocktail, neon sign) and tightens the frame to just the bartender, so he
  // can be dropped into a larger bar scene that supplies its own counter. The
  // frame is cut at the counter line (y≈168) so his cropped base sits behind
  // the scene's counter with his hands resting on top.
  const vb = isScene ? "0 0 280 220" : "40 0 200 168";
  const ratio = isScene ? 220 / 280 : 168 / 200;
  const height = Math.round(size * ratio);
  return (
    <svg
      width={size}
      height={height}
      viewBox={vb}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="RUSH AI Bartender"
    >
      <title>RUSH AI Bartender</title>
      <defs>
        <linearGradient id="nb-skin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffe2c6" />
          <stop offset="1" stopColor="#e7b184" />
        </linearGradient>
        <linearGradient id="nb-skin2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f3c096" />
          <stop offset="1" stopColor="#d99a6e" />
        </linearGradient>
        <linearGradient id="nb-hair" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#5a4a3a" />
          <stop offset="0.55" stopColor="#34281d" />
          <stop offset="1" stopColor="#1d140d" />
        </linearGradient>
        <linearGradient id="nb-vest" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#28323f" />
          <stop offset="1" stopColor="#0b0f18" />
        </linearGradient>
        <linearGradient id="nb-shirt" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#cdd5e0" />
        </linearGradient>
        <linearGradient id="nb-green" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8af36a" />
          <stop offset="1" stopColor="#41b827" />
        </linearGradient>
        <linearGradient id="nb-red" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e23a4e" />
          <stop offset="1" stopColor="#9e0f22" />
        </linearGradient>
        <linearGradient id="nb-metal" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#e8eef4" />
          <stop offset="0.5" stopColor="#9fb0c2" />
          <stop offset="1" stopColor="#5c6b7c" />
        </linearGradient>
        <linearGradient id="nb-wood-top" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#b07e4e" />
          <stop offset="1" stopColor="#7c5433" />
        </linearGradient>
        <linearGradient id="nb-wood" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5d3d22" />
          <stop offset="1" stopColor="#2c1a0d" />
        </linearGradient>
        <radialGradient id="nb-drink" cx="50%" cy="28%" r="85%">
          <stop offset="0" stopColor="#caffa6" />
          <stop offset="1" stopColor="#3aa81f" />
        </radialGradient>
        <radialGradient id="nb-eye" cx="50%" cy="32%" r="68%">
          <stop offset="0" stopColor="#3a8b49" />
          <stop offset="1" stopColor="#10321a" />
        </radialGradient>
      </defs>

      {isScene && (
        <>
      {/* Neon RUSH sign — occasional flicker */}
      <g>
        <rect x="220" y="14" width="56" height="28" rx="9" fill="#07120a" opacity="0.75" stroke="#2c7a14" strokeWidth="1" />
        <g>
          <animate attributeName="opacity" values="1;1;0.45;1;1;0.6;1;1" keyTimes="0;0.30;0.32;0.34;0.62;0.64;0.66;1" dur="8s" repeatCount="indefinite" />
          <text x="248" y="33" fontSize="13" fontWeight="800" letterSpacing="2" fill="none" stroke="#54c633" strokeWidth="3.5" strokeOpacity="0.3" textAnchor="middle" fontFamily="sans-serif">RUSH</text>
          <text x="248" y="33" fontSize="13" fontWeight="800" letterSpacing="2" fill="#8af36a" textAnchor="middle" fontFamily="sans-serif">RUSH</text>
        </g>
      </g>

      {/* Back-bar shelf with backlit bottles */}
      <ellipse cx="38" cy="122" rx="36" ry="9" fill="#54c633" opacity="0.12" />
      <rect x="14" y="94" width="11" height="30" rx="3" fill="#16301f" />
      <rect x="17.5" y="82" width="4" height="13" rx="1.5" fill="#16301f" />
      <rect x="16.8" y="78" width="5.4" height="5" rx="1.5" fill="#6fd94a" />
      <line x1="17" y1="98" x2="17" y2="118" stroke="#7ee65a" strokeOpacity="0.35" strokeWidth="1.5" />
      <rect x="30" y="88" width="12" height="36" rx="3" fill="#1b2a3d" />
      <rect x="34" y="76" width="4.5" height="13" rx="1.5" fill="#1b2a3d" />
      <rect x="33.2" y="72" width="6" height="5" rx="1.5" fill="#9fb0c2" />
      <line x1="33.5" y1="93" x2="33.5" y2="118" stroke="#bcd6ee" strokeOpacity="0.3" strokeWidth="1.5" />
      <rect x="48" y="98" width="10" height="26" rx="3" fill="#3a2412" />
      <rect x="51" y="88" width="4" height="11" rx="1.5" fill="#3a2412" />
      <rect x="50.2" y="84" width="5.6" height="5" rx="1.5" fill="#d9a45a" />
      <line x1="51" y1="102" x2="51" y2="118" stroke="#e8c79a" strokeOpacity="0.3" strokeWidth="1.5" />
      <rect x="6" y="124" width="64" height="5" rx="2" fill="#241409" />
      <rect x="6" y="124" width="64" height="1.4" fill="#6fd94a" opacity="0.3" />
        </>
      )}

      {/* Character (behind the counter). The outer scale(0.78) trims the whole
          figure; an extra inner scale on <g data-part="body"> narrows just the
          body (shoulders, torso, arms, neck) so he reads lean without shrinking
          the head. Both scale about the body centre (local x=100). */}
      <g data-part="character" transform="translate(40 -26) translate(100 0) scale(0.78 1) translate(-100 0)">
        {/* Body — extra horizontal squeeze; the head (below) stays full-size. */}
        <g data-part="body" transform="translate(100 0) scale(0.82 1) translate(-100 0)">
        {/* Uniform */}
        <path d="M18 210 C 18 178 46 169 72 163 L 128 163 C 154 169 182 178 182 210 Z" fill="url(#nb-vest)" />
        <path d="M84 161 L 100 200 L 116 161 L 116 157 L 84 157 Z" fill="url(#nb-shirt)" />
        <path d="M84 161 L 100 197 L 72 173 L 72 165 Z" fill="#141a24" />
        <path d="M116 161 L 100 197 L 128 173 L 128 165 Z" fill="#141a24" />
        <path d="M85 158 L 100 182 L 96 158 Z" fill="#ffffff" />
        <path d="M115 158 L 100 182 L 104 158 Z" fill="#eef2f7" />

        {/* Red bow tie */}
        <g data-part="bow-tie" transform="translate(100 162)">
          <path d="M0 0 L -19 -8 Q -24 0 -19 8 Z" fill="url(#nb-red)" stroke="#7a0c1c" strokeWidth="1" />
          <path d="M0 0 L 19 -8 Q 24 0 19 8 Z" fill="url(#nb-red)" stroke="#7a0c1c" strokeWidth="1" />
          <rect x="-4" y="-5" width="8" height="10" rx="2" fill="#b51228" stroke="#7a0c1c" strokeWidth="1" />
        </g>

        {/* RUSH lapel pin */}
        <circle cx="68" cy="176" r="5.2" fill="url(#nb-green)" />
        <text x="68" y="178.6" fontSize="6.4" fontWeight="700" fill="#08210a" textAnchor="middle" fontFamily="sans-serif">R</text>

        {/* Left arm — resting on the counter (hand drawn in front of it).
            Lighter underlay = rim light so the dark sleeve reads on dark bg. */}
        <path d="M56 168 C 46 174 40 180 37 190" stroke="#44566a" strokeWidth="15" strokeLinecap="round" fill="none" />
        <path d="M56 168 C 46 174 40 180 37 190" stroke="#1c2530" strokeWidth="12" strokeLinecap="round" fill="none" />

        {/* Right arm — shaking a cocktail shaker */}
        <path d="M144 167 C 158 171 168 178 172 188" stroke="#44566a" strokeWidth="15" strokeLinecap="round" fill="none" />
        <path d="M144 167 C 158 171 168 178 172 188" stroke="#1c2530" strokeWidth="12" strokeLinecap="round" fill="none" />
        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 172 188; 0 172 188; -8 172 188; 7 172 188; -8 172 188; 7 172 188; -5 172 188; 0 172 188; 0 172 188"
            keyTimes="0;0.52;0.57;0.62;0.67;0.72;0.77;0.82;1"
            dur="4.8s"
            repeatCount="indefinite"
          />
          <path d="M172 188 L179 153" stroke="#44566a" strokeWidth="14" strokeLinecap="round" fill="none" />
          <path d="M172 188 L179 153" stroke="#1c2530" strokeWidth="11" strokeLinecap="round" fill="none" />
          {/* Shaker */}
          <rect x="170" y="110" width="18" height="36" rx="8" fill="url(#nb-metal)" />
          <rect x="173.5" y="101" width="11" height="11" rx="4" fill="#7c8b9c" />
          <rect x="176.5" y="97" width="5" height="5" rx="2" fill="#5c6b7c" />
          <line x1="175" y1="114" x2="175" y2="140" stroke="#f4f8fb" strokeOpacity="0.55" strokeWidth="2" />
          {/* Cuff + hand gripping the shaker */}
          <circle cx="178" cy="159" r="6.5" fill="#eef2f7" />
          <circle cx="179" cy="149" r="7" fill="url(#nb-skin2)" />
          <path d="M173.5 147 q2 1.6 4 1.8 M174 151 q2 1.4 3.8 1.5" stroke="#c2895f" strokeWidth="0.9" strokeOpacity="0.6" fill="none" />
        </g>
        {/* Shake sparkles — synced to the shake burst */}
        <path d="M160 96 l1.2 3 3 1.2 -3 1.2 -1.2 3 -1.2 -3 -3 -1.2 3 -1.2 Z" fill="#eaffd9">
          <animate attributeName="opacity" values="0;0;1;0;1;0;0" keyTimes="0;0.55;0.62;0.68;0.74;0.8;1" dur="4.8s" repeatCount="indefinite" />
        </path>
        <path d="M198 108 l1 2.6 2.6 1 -2.6 1 -1 2.6 -1 -2.6 -2.6 -1 2.6 -1 Z" fill="#eaffd9">
          <animate attributeName="opacity" values="0;0;1;0;1;0;0" keyTimes="0;0.58;0.65;0.71;0.77;0.83;1" dur="4.8s" repeatCount="indefinite" />
        </path>

        {/* Neck (extended up behind the head so the bob never shows a gap) */}
        <rect x="90" y="126" width="20" height="36" rx="6" fill="url(#nb-skin2)" />
        <path d="M90 150 Q100 158 110 150 L110 158 L90 158 Z" fill="#c98f64" opacity="0.45" />
        </g>

        {/* Head — gentle bob (head only, so the hands stay planted) */}
        <g>
          <animateTransform attributeName="transform" type="translate" values="0 0; 0 -2.4; 0 0" dur="4.2s" repeatCount="indefinite" />

          <ellipse cx="100" cy="94" rx="42" ry="48" fill="url(#nb-skin)" />
          <path d="M62 96 Q60 128 84 140 Q70 118 66 94 Z" fill="#d99a6e" opacity="0.33" />
          <path d="M138 96 Q140 128 116 140 Q130 118 134 94 Z" fill="#d99a6e" opacity="0.28" />
          <ellipse cx="92" cy="74" rx="19" ry="11" fill="#fff0df" opacity="0.4" />

          {/* Ears */}
          <ellipse cx="58" cy="98" rx="6" ry="11" fill="url(#nb-skin2)" />
          <ellipse cx="142" cy="98" rx="6" ry="11" fill="url(#nb-skin2)" />

          {/* Hair */}
          <path d="M57 90 C 52 54 76 34 100 34 C 124 34 148 54 143 90 C 138 70 126 60 112 57 C 122 64 126 74 125 84 C 116 70 108 66 100 66 C 92 66 84 70 75 84 C 74 74 78 64 88 57 C 74 60 62 70 57 90 Z" fill="url(#nb-hair)" />
          <path d="M100 38 C 116 40 130 52 132 64 C 124 54 112 48 100 48 Z" fill="#6b5743" opacity="0.5" />

          {/* Brows */}
          <path d="M72 88 Q83 82 93 87" stroke="#2b2118" strokeWidth="3.2" strokeLinecap="round" fill="none" />
          <path d="M107 87 Q117 82 128 88" stroke="#2b2118" strokeWidth="3.2" strokeLinecap="round" fill="none" />

          {/* Eyes — AI green iris, occasional side glances */}
          <ellipse cx="84" cy="100" rx="8.5" ry="6.2" fill="#fff" />
          <ellipse cx="116" cy="100" rx="8.5" ry="6.2" fill="#fff" />
          <g>
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0 0; 0 0; 1.8 0; 1.8 0; 0 0; 0 0; -1.6 0.3; -1.6 0.3; 0 0; 0 0"
              keyTimes="0;0.18;0.22;0.34;0.38;0.55;0.59;0.72;0.76;1"
              dur="9s"
              repeatCount="indefinite"
            />
            <circle cx="85" cy="100" r="4.5" fill="url(#nb-eye)" />
            <circle cx="115" cy="100" r="4.5" fill="url(#nb-eye)" />
            <circle cx="85" cy="100" r="1.9" fill="#06120a" />
            <circle cx="115" cy="100" r="1.9" fill="#06120a" />
            <circle cx="86.6" cy="98" r="1.5" fill="#fff" />
            <circle cx="116.6" cy="98" r="1.5" fill="#fff" />
            <circle cx="85" cy="100" r="4.5" fill="none" stroke="#7ee65a" strokeWidth="0.8" strokeOpacity="0.7" />
            <circle cx="115" cy="100" r="4.5" fill="none" stroke="#7ee65a" strokeWidth="0.8" strokeOpacity="0.7" />
          </g>

          {/* Blink */}
          <rect x="75" y="93" width="18" height="0" rx="3" fill="url(#nb-skin)">
            <animate attributeName="height" values="0;0;13;0;0" keyTimes="0;0.93;0.96;0.99;1" dur="5s" repeatCount="indefinite" />
          </rect>
          <rect x="107" y="93" width="18" height="0" rx="3" fill="url(#nb-skin)">
            <animate attributeName="height" values="0;0;13;0;0" keyTimes="0;0.93;0.96;0.99;1" dur="5s" repeatCount="indefinite" />
          </rect>

          {/* Nose */}
          <path d="M100 104 Q95 117 100 122 Q105 117 100 104" fill="#d99a6e" opacity="0.4" />
          <ellipse cx="96.5" cy="121" rx="1.3" ry="0.9" fill="#c2895f" opacity="0.55" />
          <ellipse cx="103.5" cy="121" rx="1.3" ry="0.9" fill="#c2895f" opacity="0.55" />

          {/* Smile */}
          <path d="M86 131 Q100 144 114 131" stroke="#7c2d12" strokeWidth="2.6" strokeLinecap="round" fill="none">
            <animate attributeName="d" values="M86 131 Q100 142 114 131; M86 131 Q100 147 114 131; M86 131 Q100 142 114 131" dur="3s" repeatCount="indefinite" />
          </path>
          <path d="M90 133 Q100 139 110 133 Q100 135 90 133 Z" fill="#fff" opacity="0.85" />

          {/* Cheeks — subtle, kept small so the face reads lean not chubby */}
          <ellipse cx="78" cy="120" rx="4.5" ry="3" fill="#f87171" opacity="0.12" />
          <ellipse cx="122" cy="120" rx="4.5" ry="3" fill="#f87171" opacity="0.12" />
        </g>
      </g>

      {isScene && (
        <>
      {/* Bar counter */}
      <rect x="2" y="158" width="276" height="14" rx="3" fill="url(#nb-wood-top)" />
      <rect x="2" y="158" width="276" height="2.2" rx="1.1" fill="#e0b083" opacity="0.55" />
      <ellipse cx="100" cy="165" rx="70" ry="3.4" fill="#ffffff" opacity="0.07" />
      <rect x="2" y="172" width="276" height="48" rx="3" fill="url(#nb-wood)" />
      {/* Under-counter neon strip (fake glow: wide faint line under a bright one) */}
      <line x1="8" y1="176.5" x2="272" y2="176.5" stroke="#6fd94a" strokeWidth="6" strokeOpacity="0.18">
        <animate attributeName="stroke-opacity" values="0.18;0.1;0.18" dur="6s" repeatCount="indefinite" />
      </line>
      <line x1="8" y1="176.5" x2="272" y2="176.5" stroke="#6fd94a" strokeWidth="2" strokeOpacity="0.9">
        <animate attributeName="stroke-opacity" values="0.9;0.55;0.9" dur="6s" repeatCount="indefinite" />
      </line>
      <line x1="10" y1="194" x2="270" y2="194" stroke="#1c0f08" strokeOpacity="0.5" strokeWidth="1" />
      <line x1="10" y1="208" x2="270" y2="208" stroke="#1c0f08" strokeOpacity="0.5" strokeWidth="1" />

      {/* Served cocktail on the counter — rising bubbles + sparkle */}
      <ellipse cx="44" cy="164.5" rx="14" ry="2" fill="#000000" opacity="0.25" />
      <path d="M31.5 135.5 L56.5 135.5 L45 150.5 L43 150.5 Z" fill="url(#nb-drink)" />
      <circle cx="42.5" cy="147" r="1.3" fill="#eaffd9">
        <animate attributeName="cy" values="147;137" dur="2.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.9;0" keyTimes="0;0.25;1" dur="2.6s" repeatCount="indefinite" />
      </circle>
      <circle cx="44.5" cy="147" r="1.6" fill="#eaffd9">
        <animate attributeName="cy" values="147;137" dur="3.2s" begin="1.1s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.9;0" keyTimes="0;0.25;1" dur="3.2s" begin="1.1s" repeatCount="indefinite" />
      </circle>
      <circle cx="46" cy="147" r="1.1" fill="#eaffd9">
        <animate attributeName="cy" values="147;138" dur="2.9s" begin="0.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.9;0" keyTimes="0;0.25;1" dur="2.9s" begin="0.6s" repeatCount="indefinite" />
      </circle>
      <path d="M28 132 L60 132 L46 152 L42 152 Z" fill="none" stroke="#dffcce" strokeOpacity="0.85" strokeWidth="1.3" />
      <ellipse cx="44" cy="132" rx="16.2" ry="2.4" fill="#eaffd9" opacity="0.85" />
      <rect x="43" y="152" width="2" height="11" fill="#cdebbc" />
      <rect x="35" y="162.5" width="18" height="2.6" rx="1.3" fill="#cdebbc" />
      <circle cx="59.5" cy="130.5" r="4.2" fill="#8af36a" stroke="#2c7a14" strokeWidth="0.8" />
      <circle cx="59.5" cy="130.5" r="2.4" fill="#d9ffc0" />
      <path d="M26 114 l1.2 3 3 1.2 -3 1.2 -1.2 3 -1.2 -3 -3 -1.2 3 -1.2 Z" fill="#eaffd9">
        <animate attributeName="opacity" values="0.15;1;0.15" dur="2.1s" repeatCount="indefinite" />
      </path>
        </>
      )}

      {/* Left hand resting on the counter — scene only; in the character
          variant the CSS bar supplies the counter, so the sleeve just meets it. */}
      {isScene && (
        <>
          <ellipse cx="75" cy="164.5" rx="8" ry="1.8" fill="#000000" opacity="0.2" />
          <ellipse cx="78.5" cy="156.5" rx="5.6" ry="4" fill="#eef2f7" />
          <ellipse cx="75.5" cy="161" rx="7.2" ry="5" fill="url(#nb-skin2)" />
          <path d="M71 162 q1.6 1.4 3.4 1.6 M75.4 163.8 q1.8 0.2 3.4 -0.6" stroke="#c2895f" strokeWidth="0.8" strokeOpacity="0.6" fill="none" />
        </>
      )}
    </svg>
  );
}
